import { useEffect, useState, type FormEvent } from "react"
import { createStyles } from "./FormPopup.styles"
import { getInjectedCss } from "./FormPopup.injected.css"
import { useFormPopupVisibility } from "./hooks/useFormPopupVisibility"
import { useDeferredReady } from "./hooks/useDeferredReady"
import { parseStates } from "./parseStates"
import { resolveRecaptchaText } from "./resolveRecaptchaText"
import { readHostAttribute, resolveHostFlag } from "./getWebComponentHost"
import {
  communicationConsentKey,
  parseCommunicationConsents,
} from "./parseCommunicationConsents"
import { submitHubSpotForm } from "./api/submitHubSpotForm"
import { formTypeFields, resolveFormType } from "./formType"
import { useInvisibleRecaptcha } from "./recaptcha/useInvisibleRecaptcha"
import FormScreen from "./screens/FormScreen"
import ConfirmationScreen from "./screens/ConfirmationScreen"
import type { FormPopupProps } from "./types"

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function isDemoPortal(portalId: string): boolean {
  return !portalId || portalId === "demo"
}

function isValidPhone(value: string): boolean {
  const digits = value.replace(/\D/g, "")
  return digits.length >= 10 && digits.length <= 15
}

const FormPopup = ({
  hubspotPortalId,
  hubspotFormId,
  formType: formTypeProp,
  emailProperty = "email",
  firstNameProperty = "firstname",
  phoneProperty = "phone",
  stateProperty = "state",
  additionalPropertyName,
  additionalPropertyValue,
  consentToProcessText,
  communicationConsents,
  formTitle,
  formSubtitle,
  states,
  submitButtonText = "Submit",
  emailPlaceholder = "Email *",
  firstNamePlaceholder = "First name *",
  phonePlaceholder = "Phone *",
  stateLabel = "Select A State *",
  confirmationTitle,
  confirmationMessage,
  confirmationButtonText,
  confirmationButtonUrl,
  recaptchaText,
  recaptchaSiteKey,
  backgroundColor,
  overlayColor,
  textColor = "#111111",
  buttonColor = "#000000",
  buttonTextColor = "#ffffff",
  buttonHoverColor = "#333333",
  buttonHoverTextColor = "#ffffff",
  fontFamily,
  headingFontFamily,
  waitForAgeGate,
  waitForStorageKey,
  waitForStorageValue,
  waitUntilHidden,
  waitUntilHiddenHost,
  container,
}: FormPopupProps & { container?: HTMLElement }) => {
  const resolvedRecaptchaSiteKey =
    recaptchaSiteKey?.trim() ||
    readHostAttribute(container, "recaptcha-site-key")?.trim() ||
    ""
  const recaptchaEnabled = Boolean(resolvedRecaptchaSiteKey)

  const formType = resolveFormType(
    formTypeProp || readHostAttribute(container, "form-type")
  )
  const fields = formTypeFields(formType)
  const styles = createStyles({
    backgroundColor,
    overlayColor,
    textColor,
    buttonColor,
    buttonTextColor,
    fontFamily,
    headingFontFamily,
    recaptchaEnabled,
  })

  const resolvedFormId =
    hubspotFormId?.trim() ||
    readHostAttribute(container, "hubspot-form-id")?.trim() ||
    ""
  const resolvedPortalId =
    hubspotPortalId?.trim() ||
    readHostAttribute(container, "hubspot-portal-id")?.trim() ||
    ""

  const resolvedWaitForAgeGate = resolveHostFlag(
    waitForAgeGate,
    container,
    "wait-for-age-gate"
  )
  const resolvedWaitForStorageKey =
    waitForStorageKey?.trim() ||
    readHostAttribute(container, "wait-for-storage-key")?.trim() ||
    ""
  const resolvedWaitForStorageValue =
    waitForStorageValue?.trim() ||
    readHostAttribute(container, "wait-for-storage-value")?.trim() ||
    ""
  const resolvedWaitUntilHidden =
    waitUntilHidden?.trim() ||
    readHostAttribute(container, "wait-until-hidden")?.trim() ||
    ""
  const resolvedWaitUntilHiddenHost =
    waitUntilHiddenHost?.trim() ||
    readHostAttribute(container, "wait-until-hidden-host")?.trim() ||
    ""

  const { showModal, markSubmitted, dismiss } =
    useFormPopupVisibility(resolvedFormId)
  const gateReady = useDeferredReady({
    waitForAgeGate: resolvedWaitForAgeGate,
    waitForStorageKey: resolvedWaitForStorageKey,
    waitForStorageValue: resolvedWaitForStorageValue,
    waitUntilHidden: resolvedWaitUntilHidden,
    waitUntilHiddenHost: resolvedWaitUntilHiddenHost,
  })
  const visible = showModal && gateReady
  const { execute: executeRecaptcha } = useInvisibleRecaptcha(
    resolvedRecaptchaSiteKey || undefined,
    visible
  )

  const [step, setStep] = useState<"form" | "confirmation">("form")
  const [firstName, setFirstName] = useState("")
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [state, setState] = useState("")
  const [consentToProcess, setConsentToProcess] = useState(false)
  const [communicationValues, setCommunicationValues] = useState<
    Record<string, boolean>
  >({})
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")

  const stateOptions = parseStates(states)
  const recaptchaDisclaimer = resolveRecaptchaText(recaptchaText, container)
  const processingConsentText = consentToProcessText?.trim() || ""
  const communicationOptions = parseCommunicationConsents(communicationConsents)
  const hasLegalConsent =
    Boolean(processingConsentText) || communicationOptions.length > 0

  useEffect(() => {
    if (!visible) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [visible])

  useEffect(() => {
    if (!visible) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") dismiss()
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [visible, dismiss])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmedFirstName = firstName.trim()
    const trimmedEmail = email.trim()
    const trimmedPhone = phone.trim()

    if (fields.firstName && !trimmedFirstName) {
      setError("Please enter your first name.")
      return
    }
    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setError("Please enter a valid email address.")
      return
    }
    if (fields.phone && !isValidPhone(trimmedPhone)) {
      setError("Please enter a valid phone number.")
      return
    }
    if (fields.state && !state) {
      setError("Please select a state.")
      return
    }
    if (processingConsentText && !consentToProcess) {
      setError("Please agree to the privacy terms.")
      return
    }
    const missingCommunication = communicationOptions.find(
      (consent) =>
        consent.required && !communicationValues[communicationConsentKey(consent)]
    )
    if (missingCommunication) {
      setError("Please agree to the required communications terms.")
      return
    }

    setError("")
    setSubmitting(true)

    const fieldsToSubmit = [{ name: emailProperty, value: trimmedEmail }]
    if (fields.firstName) {
      fieldsToSubmit.push({ name: firstNameProperty, value: trimmedFirstName })
    }
    if (fields.phone) {
      fieldsToSubmit.push({ name: phoneProperty, value: trimmedPhone })
    }
    if (fields.state) {
      fieldsToSubmit.push({ name: stateProperty, value: state })
    }
    if (additionalPropertyName?.trim() && additionalPropertyValue != null) {
      fieldsToSubmit.push({
        name: additionalPropertyName.trim(),
        value: additionalPropertyValue,
      })
    }

    try {
      if (recaptchaEnabled) {
        await executeRecaptcha()
      }
      if (!isDemoPortal(resolvedPortalId)) {
        await submitHubSpotForm({
          portalId: resolvedPortalId,
          formId: resolvedFormId,
          fields: fieldsToSubmit,
          legalConsent: hasLegalConsent
            ? {
                consentToProcess: Boolean(
                  processingConsentText && consentToProcess
                ),
                ...(processingConsentText
                  ? { text: processingConsentText }
                  : {}),
                ...(communicationOptions.length
                  ? {
                      communications: communicationOptions.map((consent) => ({
                        subscriptionTypeId: Number(consent.subscriptionTypeId),
                        text: consent.text,
                        value: Boolean(
                          communicationValues[communicationConsentKey(consent)]
                        ),
                      })),
                    }
                  : {}),
              }
            : undefined,
        })
      }
      markSubmitted()
      setStep("confirmation")
    } catch (submitError) {
      const message =
        submitError instanceof Error ? submitError.message : ""
      if (message.includes("reCAPTCHA")) {
        setError("Please complete the reCAPTCHA and try again.")
      } else {
        setError("Something went wrong. Please try again.")
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleConfirmationClick = () => {
    if (confirmationButtonUrl) {
      window.location.href = confirmationButtonUrl
      return
    }
    dismiss()
  }

  if (!visible) return null

  const labelledBy =
    step === "form" ? "form-popup-form-title" : "form-popup-confirmation-title"

  const clearError = () => {
    if (error) setError("")
  }

  return (
    <div
      id="form-popup-overlay"
      style={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      onClick={(event) => {
        if (event.target === event.currentTarget && !submitting) dismiss()
      }}
    >
      <style>
        {getInjectedCss({
          buttonHoverColor,
          buttonHoverTextColor,
        })}
      </style>
      <div id="form-popup-card" style={styles.card} role="document">
        <button
          id="form-popup-close"
          type="button"
          style={styles.closeButton}
          aria-label="Close"
          onClick={dismiss}
        >
          <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
            <path
              d="M1 1l8 8M9 1L1 9"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {step === "form" ? (
          <FormScreen
            styles={styles}
            formType={formType}
            formTitle={formTitle}
            formSubtitle={formSubtitle}
            firstName={firstName}
            firstNamePlaceholder={firstNamePlaceholder}
            email={email}
            emailPlaceholder={emailPlaceholder}
            phone={phone}
            phonePlaceholder={phonePlaceholder}
            state={state}
            stateLabel={stateLabel}
            states={stateOptions}
            consentToProcessText={processingConsentText || undefined}
            consentToProcess={consentToProcess}
            communicationConsents={communicationOptions}
            communicationValues={communicationValues}
            submitButtonText={submitButtonText}
            recaptchaText={recaptchaDisclaimer}
            recaptchaBranding={recaptchaEnabled}
            error={error}
            submitting={submitting}
            onFirstNameChange={(value) => {
              setFirstName(value)
              clearError()
            }}
            onEmailChange={(value) => {
              setEmail(value)
              clearError()
            }}
            onPhoneChange={(value) => {
              setPhone(value)
              clearError()
            }}
            onStateChange={(value) => {
              setState(value)
              clearError()
            }}
            onConsentToProcessChange={(value) => {
              setConsentToProcess(value)
              clearError()
            }}
            onCommunicationChange={(key, value) => {
              setCommunicationValues((current) => ({
                ...current,
                [key]: value,
              }))
              clearError()
            }}
            onSubmit={handleSubmit}
          />
        ) : (
          <ConfirmationScreen
            styles={styles}
            confirmationTitle={confirmationTitle}
            confirmationMessage={confirmationMessage}
            confirmationButtonText={confirmationButtonText}
            onButtonClick={handleConfirmationClick}
          />
        )}
      </div>
    </div>
  )
}

export default FormPopup
